import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PasswordResetConfirm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate('/login'), 2000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${apiBase}/api/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });

      if (response.ok) {
        setSuccess(true);
      } else if (response.status === 400) {
        setError('This reset link has expired or already been used');
      } else if (response.status === 422) {
        setError('Password does not meet requirements');
      } else {
        setError('Something went wrong, please try again');
      }
    } catch {
      setError('Something went wrong, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <div className="login-brand">
              <h1>Invalid Link</h1>
              <p>This password reset link is missing or malformed.</p>
            </div>
          </div>
          <div className="auth-modal-actions">
            <Link to="/login" className="auth-button text">Back to Login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-brand">
            <h1>Set New Password</h1>
            <p>Enter your new password below</p>
          </div>
        </div>

        <div className="login-form-container">
          {success ? (
            <div className="auth-modal-content">
              <p className="auth-modal-message" style={{ color: '#16a34a', fontWeight: 500, textAlign: 'center' }}>
                Password updated. Redirecting to login…
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              {error && (
                <div className="error-message" role="alert">
                  {error}
                </div>
              )}

              <input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isSubmitting}
                autoComplete="new-password"
              />

              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isSubmitting}
                autoComplete="new-password"
              />

              <button
                type="submit"
                disabled={isSubmitting || !newPassword || !confirmPassword}
                className="auth-button primary"
              >
                {isSubmitting ? 'Updating…' : 'Update Password'}
              </button>

              <div className="auth-modal-actions">
                <Link to="/login" className="auth-button text">Back to Login</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetConfirm;
